import type { ProductNode } from "@/entities/product/api/productService";

import type { VideoEntity } from "../api/videoService";

/**
 * 「从产品库选」的纯逻辑(波3-①/⑤,2026-07-26)。
 *
 * 产品图来源从「用户本次上传」扩到「产品树预置」——  少的是**传图**这一步,不是全部输入。
 *
 * ★**边界说准**(codex 第三轮提醒):本波之后文案框(`userInput`)**仍然是必填**,
 * 选了产品也不能空手编排 —— 引擎要有东西可讲。「零输入」在波3 的范围里指的是
 * 「产品图不用现传」,不是「什么都不用填」。真正的空手出片要 ai 侧按产品树合成 brief,
 * 那是下一步(设计稿 §8 列在「不做」里)。这里不预告做不到的事。
 *
 * ★这里**不过 VLM 分类**:产品树来的图角色已知(就是 product),再过一次 classify 是白花钱,
 * 且会引入分类错误(上传路要分类是因为那批图什么都可能是)。
 */

/**
 * 有预置图、可以直接拿来出片的产品节点。
 *
 * ★**排除 `visibility: "internal"`**(codex 第二轮抓的):那个标记的语义是「在售但只给内部看」——
 * 产品树的既有用法是「internal 节点的事实块不进公开客服/官网检索」。成片是对外物料,
 * 比客服回答还外;内部款的实拍图进了成片,等于绕开这道治理轴。
 * 缺省当 public(老数据/老后端可能没有这个字段,与消费侧一贯口径一致)。
 */
export function productsWithImages(nodes: ProductNode[] | null | undefined): ProductNode[] {
  return (nodes ?? []).filter(
    (n) =>
      n.type === "product" &&
      n.status === 1 &&
      n.visibility !== "internal" &&
      (n.sampleImages?.length ?? 0) > 0,
  );
}

/**
 * VID-PICK-2(2026-08-18 老板报障「不能选产品树」):可**选品**的产品 —— 不再要求挂图。
 *
 * VID-BRIEF-1 的「选品带卖点」骑在图选择器上,而真实租户(老板店)产品树卖点齐全、
 * `sampleImages` 全空 → 选择器恒空态,整个选品对无图产品不可达。选品的最低要件是
 * 「树上有这个在售产品」,图只是加分项:有图=带图+卖点,无图=只带卖点(图去产品树补)。
 * internal 仍排除(成片是对外物料,治理轴同 productsWithImages)。
 */
export function briefableProducts(nodes: ProductNode[] | null | undefined): ProductNode[] {
  return (nodes ?? []).filter(
    (n) => n.type === "product" && n.status === 1 && n.visibility !== "internal",
  );
}

/** 该节点有没有可用预置图(选品分流:有图=图+卖点,无图=只带卖点)。 */
export function hasSampleImage(node: ProductNode): boolean {
  return (node.sampleImages?.length ?? 0) > 0;
}

/**
 * 整棵树里到底有没有图 —— 决定「从产品库选」是给列表还是给空态。
 *
 * ★空态必须是**真空态**而不是灰按钮:今天点进去看到的是一棵有名字没图的树,
 * 用户不知道该去哪补。`sample_images` 这一列此前零写入者,所以「树上没图」是常态不是异常。
 */
export function hasAnyProductImage(nodes: ProductNode[] | null | undefined): boolean {
  return productsWithImages(nodes).length > 0;
}

/** 下一个可用实体 id(E1..En 连续,与上传路的 reindex 同规则)。 */
function nextEntityId(entities: VideoEntity[]): string {
  return `E${entities.length + 1}`;
}

/**
 * 产品节点 → 图背书产品实体。已经选过同一张图就原样返回(不重复加)。
 *
 * `confidence: 1` 是**事实不是乐观**:这张图的角色不是猜出来的,是用户在产品树上挂的。
 * 上传路的低置信高亮(`isUncertain`)因此不会误报到产品库来源上。
 */
export function addProductFromLibrary(
  entities: VideoEntity[],
  node: ProductNode,
  imageKey?: string,
): VideoEntity[] {
  const key = imageKey ?? node.sampleImages?.[0] ?? "";
  if (!key) return entities;
  if (entities.some((e) => e.key === key)) return entities;
  const entity: VideoEntity = {
    id: nextEntityId(entities),
    role: "product",
    source: "image",
    key,
    productId: node.code,
    name: node.name,
    confidence: 1,
  };
  return [...entities, entity];
}

/**
 * 这批实体里,哪些是「本店产品图」(来自产品树)——  用于创建页如实说明来源。
 *
 * 判据是 `productId` 非空且图背书:上传路的实体 `productId` 恒空(分类器不知道对应哪个 SKU),
 * 产品库来源才带节点码。★不能用「有没有 key」推,那两条路都有 key。
 */
export function libraryBackedEntities(entities: VideoEntity[] | null | undefined): VideoEntity[] {
  return (entities ?? []).filter(
    (e) => e.source === "image" && e.role === "product" && !!e.key && !!e.productId,
  );
}
